import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MailPlus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ChaserComposer({ clientId, jobId, contacts, onCreated, buttonLabel = "Send chaser" }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [contactId, setContactId] = useState("");
  const [templateKey, setTemplateKey] = useState("chaser_records_request");
  const [draft, setDraft] = useState(null); // returned Chaser doc after create
  const emailContacts = (contacts || []).filter((c) => !!c.email);

  useEffect(() => {
    api.get("/email-templates").then((r) => {
      const chaser = r.data.filter((t) => t.key.startsWith("chaser_"));
      setTemplates(chaser);
    });
  }, []);

  useEffect(() => {
    if (open && emailContacts.length && !contactId) {
      const primary = emailContacts.find((c) => c.is_primary);
      setContactId((primary || emailContacts[0]).contact_id);
    }
  }, [open, emailContacts, contactId]);

  const createDraft = async () => {
    if (!contactId) return toast.error("Pick a contact with an email address");
    try {
      const r = await api.post("/chasers", {
        client_id: clientId,
        job_id: jobId || null,
        contact_id: contactId,
        template_key: templateKey,
        portal_base_url: window.location.origin,
      });
      setDraft(r.data);
      toast.success("Draft created — edit and submit for approval");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const save = async () => {
    try {
      await api.patch(`/chasers/${draft.chaser_id}`, { subject: draft.subject, body: draft.body });
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitForApproval = async () => {
    await api.patch(`/chasers/${draft.chaser_id}`, { subject: draft.subject, body: draft.body, status: "pending_approval" });
    toast.success("Chaser submitted for approval");
    reset();
    onCreated?.();
  };

  const reset = () => {
    setOpen(false);
    setDraft(null);
    setContactId("");
    setTemplateKey("chaser_records_request");
  };

  if (!emailContacts.length) {
    return (
      <Button variant="outline" data-testid="chaser-btn-disabled" disabled title="Add a contact with an email first" className="rounded-md">
        <MailPlus className="h-4 w-4 mr-2" /> {buttonLabel}
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="chaser-btn" variant="outline" className="rounded-md">
          <MailPlus className="h-4 w-4 mr-2" /> {buttonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="chaser-composer">
        <DialogHeader><DialogTitle>{draft ? "Review chaser" : "New chaser to client"}</DialogTitle></DialogHeader>

        {!draft ? (
          <div className="space-y-3">
            <div>
              <Label>Send to (contact)</Label>
              <Select value={contactId} onValueChange={setContactId}>
                <SelectTrigger data-testid="chaser-contact-select"><SelectValue placeholder="Pick a contact…" /></SelectTrigger>
                <SelectContent>
                  {emailContacts.map((c) => (
                    <SelectItem key={c.contact_id} value={c.contact_id}>
                      {c.name} · {c.email} {c.is_primary && "(primary)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Template</Label>
              <Select value={templateKey} onValueChange={setTemplateKey}>
                <SelectTrigger data-testid="chaser-template-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button data-testid="chaser-next-btn" onClick={createDraft} className="bg-slate-900 hover:bg-slate-800">Compose</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-4 gap-2 items-center">
              <div className="text-slate-500">To</div>
              <div className="col-span-3 font-mono text-xs">{draft.to_email} {draft.to_name && <span className="text-slate-400">({draft.to_name})</span>}</div>
            </div>
            <div>
              <Label>Subject</Label>
              <Input data-testid="chaser-subject" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
            </div>
            <div>
              <Label>Body</Label>
              <Textarea data-testid="chaser-body" rows={12} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
            </div>
            <div className="text-[11px] text-slate-500">
              Review mode routes this chaser to your practice inbox · Tax identifiers are automatically redacted before send.
            </div>
            <DialogFooter>
              <Button data-testid="chaser-savedraft-btn" variant="outline" onClick={save}>Save draft</Button>
              <Button data-testid="chaser-submit-btn" onClick={submitForApproval} className="bg-slate-900 hover:bg-slate-800">Submit for approval</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
